import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { useConstant } from "@hooks/useConstant";
import { Drawer } from "@design-system/Drawer/Drawer";
import { Loader } from "@design-system/Loader/Loader";
import { Pagination } from "@design-system/Pagination/Pagination";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import {
    AuditLogChangeType,
    AuditLogDiff,
    HistoryDetailsItem,
    ResourceType,
    isHistoryDetailsResponse,
} from "@common/types/auditLog";
import { getSourceTypeTemplate } from "@common/constants/history-source-types";
import { addTimeZoneOffset } from "@helpers/date";
import { pluralizeWithCount } from "@common/helpers/string";
import { Category } from "@common/types/category";
import { FinancialAccount } from "@common/types/financialAccount";
import { Entity } from "@common/types/entity";
import { useGetHistoryDetailsQuery } from "@api/auditLog.api";
import { StepTrailBreadcrumbs } from "@components/breadcrumbs/StepTrailBreadcrumbs";
import { useHistoryDetailsListParams } from "../useHistoryDetailsListParams";
import { Button } from "@design-system/Button/Button";
import { ArrowCornerIcon } from "@icons";
import { historyDrawerContext } from "../../historyDrawerContext";
import { getShowRevertedParam } from "../../views/historyViewConfig";
import { HistoryCreatedTransactionDetails } from "../HistoryCreatedTransactionDetails";
import { ActivityDrawerHeader } from "../ActivityDrawerHeader";
import { HistoryCreatedTransactionDetailsRow } from "../transactions/views/historyCreatedTransactionDetailsViewConfig";
import historyDetailStyles from "../HistoryDetails.module.scss";
import { useTransactionSelectionActions } from "../transactions/useTransactionSelectionActions";
import {
    useReconciliationMatchingData,
    ReconciliationActionType,
    ReconciliationMetadata,
} from "./hooks/useReconciliationMatchingData";
import styles from "./GroupedHistoryDrawer.module.scss";
import {
    GroupedHistoryLogList,
    type ActionGroup,
} from "./GroupedHistoryLogList";

interface DiffValueExtractor {
    getValue: (field: string, defaultValue?: unknown) => unknown;
    getLabel: (field: string) => string | undefined;
}

const createDiffExtractor = (
    diffs: AuditLogDiff[],
    useBefore: boolean,
): DiffValueExtractor => ({
    getValue: (field: string, defaultValue?: unknown) => {
        const diff = diffs.find((d) => d.field === field);
        const ref = useBefore ? diff?.before : diff?.after;
        return ref?.value ?? defaultValue;
    },
    getLabel: (field: string) => {
        const diff = diffs.find((d) => d.field === field);
        const ref = useBefore ? diff?.before : diff?.after;
        return ref?.valueLabel as string | undefined;
    },
});

const createResourceExtractor = (
    resource: Record<string, unknown> | undefined,
): DiffValueExtractor => ({
    getValue: (field: string, defaultValue?: unknown) =>
        resource?.[field] ?? defaultValue,
    getLabel: (field: string) =>
        resource?.[`${field}Name`] as string | undefined,
});

const formatReconciliationDate = (dateString: string | null): string => {
    if (!dateString) {
        return "";
    }
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
    });
};

const getReconciliationHeaderDescription = (
    metadata: ReconciliationMetadata | null,
): string | null => {
    if (!metadata?.accountName) {
        return null;
    }

    const { accountName, openingDate, closingDate } = metadata;
    const formattedOpeningDate = formatReconciliationDate(openingDate);
    const formattedClosingDate = formatReconciliationDate(closingDate);

    if (formattedOpeningDate && formattedClosingDate) {
        return `Reconciled ${accountName} for the period ${formattedOpeningDate} - ${formattedClosingDate}`;
    }

    return `Reconciled ${accountName}`;
};

interface HeaderDescriptionOptions {
    sourceType: string;
    resourceType?: ResourceType;
    totalCount?: number;
    fields?: string[];
}

const getHeaderDescription = ({
    sourceType,
    resourceType,
    totalCount = 0,
    fields,
}: HeaderDescriptionOptions): string => {
    const template = getSourceTypeTemplate(sourceType, resourceType, fields);
    if (!template) {
        return `${totalCount} items`;
    }
    return `${template.verb} ${pluralizeWithCount(template.noun, totalCount)}`;
};

function getChangeTypeForActionType(
    actionType: ReconciliationActionType,
): AuditLogChangeType | undefined {
    switch (actionType) {
        case ReconciliationActionType.RECONCILED:
            return AuditLogChangeType.UPDATE;
        case ReconciliationActionType.ADDED:
            return AuditLogChangeType.CREATE;
        case ReconciliationActionType.REMOVED:
            return AuditLogChangeType.DELETE;
        default:
            return undefined;
    }
}

export const GroupedHistoryDrawer: React.FC = () => {
    const { open, historyRow, closeDrawer } = useContext(historyDrawerContext);
    const [isOpen, setIsOpen] = useState(false);
    const [expandedGroup, setExpandedGroup] = useState<ActionGroup | null>(
        null,
    );
    const [drillDownPage, setDrillDownPage] = useState(1);
    const [nestedRowSelection, setNestedRowSelection] =
        useState<RowSelectionState>({});

    const shouldShowContent = isOpen || open;
    const listParams = useHistoryDetailsListParams(historyRow);
    const fallbackDate = useConstant(() => new Date());

    const {
        filteredLogs,
        isLoading,
        searchTerm,
        setSearchTerm,
        currentPage,
        setCurrentPage,
        totalPages,
        reconciliationMetadata,
    } = useReconciliationMatchingData({
        historyRow,
        enabled: shouldShowContent,
    });

    const drillDownChangeType = expandedGroup
        ? getChangeTypeForActionType(expandedGroup.actionType)
        : undefined;

    const { data: drillDownResponse, isLoading: isDrillDownLoading } =
        useGetHistoryDetailsQuery({
            ...listParams,
            date: historyRow?.date ?? fallbackDate,
            sourceType: historyRow?.sourceType ?? "",
            sourceIds: historyRow?.historyItem.sourceIds,
            actorId: historyRow?.actorId,
            organizationActorId: historyRow?.historyItem.organizationActorId,
            drillDownResourceType: ResourceType.TRANSACTION,
            drillDownChangeType,
            page: drillDownPage,
            limit: 50,
            showReverted: getShowRevertedParam(historyRow),
            options: {
                enabled:
                    !!expandedGroup &&
                    !!historyRow &&
                    drillDownChangeType !== undefined,
            },
        });

    const drillDownData = useMemo<HistoryDetailsItem[]>(() => {
        if (
            !drillDownResponse ||
            !isHistoryDetailsResponse(drillDownResponse)
        ) {
            return [];
        }
        return drillDownResponse.data;
    }, [drillDownResponse]);

    const drillDownTotalPages = useMemo(() => {
        if (
            !drillDownResponse ||
            !isHistoryDetailsResponse(drillDownResponse)
        ) {
            return 1;
        }
        return drillDownResponse.totalPages;
    }, [drillDownResponse]);

    const drillDownTotal = useMemo(() => {
        if (
            !drillDownResponse ||
            !isHistoryDetailsResponse(drillDownResponse)
        ) {
            return 0;
        }
        return drillDownResponse.total;
    }, [drillDownResponse]);

    const handleClose = useCallback(() => {
        setSearchTerm("");
        setCurrentPage(1);
        setExpandedGroup(null);
        setDrillDownPage(1);
        setNestedRowSelection({});
        closeDrawer();
    }, [closeDrawer, setSearchTerm, setCurrentPage]);

    const handleExpandGroup = useCallback((group: ActionGroup) => {
        setExpandedGroup(group);
        setDrillDownPage(1);
    }, []);

    const handleCloseExpandedGroup = useCallback(() => {
        setExpandedGroup(null);
        setDrillDownPage(1);
        setNestedRowSelection({});
    }, []);

    useEffect(() => {
        setNestedRowSelection({});
    }, [expandedGroup, drillDownPage]);

    const breadcrumbSteps = useMemo(
        () => [
            { id: "parent" as const, label: "Reconciliation" },
            { id: "details" as const, label: expandedGroup?.description ?? "" },
        ],
        [expandedGroup?.description],
    );

    const transactionSummaryData = useMemo<
        HistoryCreatedTransactionDetailsRow[]
    >(() => {
        if (!expandedGroup || drillDownData.length === 0) {
            return [];
        }

        const isDeleted =
            expandedGroup.actionType === ReconciliationActionType.REMOVED;
        const isCreated =
            expandedGroup.actionType === ReconciliationActionType.ADDED;
        const useDiff = isCreated || isDeleted;
        const canOpenTransaction =
            expandedGroup.actionType === ReconciliationActionType.ADDED;

        return drillDownData.map((log: HistoryDetailsItem) => {
            const extractor = useDiff
                ? createDiffExtractor(log.diff, isDeleted)
                : createResourceExtractor(log.resource);

            const { getValue, getLabel } = extractor;

            const categoryFromResource = getValue("category") as
                | Partial<Category>
                | undefined;
            const categoryId = getValue("categoryId") as string | undefined;
            const categoryLabel = getLabel("categoryId");

            const category: Partial<Category> = categoryFromResource ?? {
                id: categoryId,
                label: categoryLabel,
            };

            const rawIdValue = getValue("id") ?? log.resourceId;
            const id =
                rawIdValue === null || rawIdValue === undefined
                    ? ""
                    : String(rawIdValue);

            return {
                canOpenTransaction,
                id,
                logDate: log.createdAt,
                date: getValue("date")
                    ? addTimeZoneOffset(new Date(getValue("date") as string))
                    : log.createdAt,
                bankDescription:
                    (getValue("bankDescription") as string) ??
                    (getValue("description") as string) ??
                    "",
                category,
                financialAccount: {
                    id: Number(getValue("financialAccountId")),
                    name: getLabel("financialAccountId"),
                } as Partial<FinancialAccount>,
                amount: (getValue("amount") as number) ?? 0,
                originalAmount: (getValue("originalAmount") as number) ?? 0,
                entity: (getValue("entity") as Partial<Entity>) ?? {},
                subaccount: getValue("subaccount"),
                ledger: {
                    accountId: getValue("accountId") as string,
                    accountLabel: getLabel("accountId"),
                },
                entityId: log.entityId,
            };
        });
    }, [expandedGroup, drillDownData]);

    const {
        selectedTransactionIds: nestedSelectedTransactionIds,
        openTransactions: handleNestedOpenTransactions,
    } = useTransactionSelectionActions({
        mode: "lifecycle",
        rows: transactionSummaryData,
        rowSelection: nestedRowSelection,
        historyRow: historyRow ?? null,
        sourceIds: historyRow?.historyItem.sourceIds,
    });

    const nestedDrillShowsBulkOpen = transactionSummaryData.some(
        (row) => row.canOpenTransaction,
    );

    const headerAddonLeft = useMemo(() => {
        if (!historyRow) {
            return null;
        }

        const reconciliationDescription = getReconciliationHeaderDescription(
            reconciliationMetadata,
        );

        const description =
            reconciliationDescription ??
            getHeaderDescription({
                sourceType: historyRow.sourceType,
                resourceType: historyRow.resourceType,
                totalCount: historyRow.count,
                fields: historyRow.fields,
            });

        return (
            <ActivityDrawerHeader
                historyRow={historyRow}
                activityTitle={description}
                dateClassName={styles.headerDate}
            />
        );
    }, [historyRow, reconciliationMetadata]);

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className={styles.loaderContainer}>
                    <Loader />
                </div>
            );
        }

        if (filteredLogs.length === 0) {
            return (
                <div className={styles.emptyContainer}>
                    <EmptyState
                        header={
                            searchTerm
                                ? "No matching items found"
                                : "No activity found"
                        }
                    />
                </div>
            );
        }

        return (
            <GroupedHistoryLogList
                logs={filteredLogs}
                onExpandGroup={handleExpandGroup}
            />
        );
    };

    const renderTransactionDetails = () => {
        if (isDrillDownLoading) {
            return (
                <div className={styles.loaderContainer}>
                    <Loader />
                </div>
            );
        }

        if (transactionSummaryData.length === 0) {
            return (
                <div className={styles.emptyContainer}>
                    <EmptyState header="No transactions found" />
                </div>
            );
        }

        const nestedTableFooter =
            expandedGroup && historyRow ? (
                <CommandIsland
                    sticky
                    className={commandIslandStyles.bulkActionsStickyFooter}
                >
                    <div className={historyDetailStyles.footerLeft}>
                        {drillDownTotalPages > 1 && (
                            <Pagination
                                size="md"
                                currentPage={drillDownPage}
                                pageCount={drillDownTotalPages}
                                onPageChange={setDrillDownPage}
                            />
                        )}
                        <CommandIslandTotal>
                            {pluralizeWithCount("activity", drillDownTotal)}
                        </CommandIslandTotal>
                    </div>
                    <div>
                        {nestedDrillShowsBulkOpen && (
                            <Button
                                variant="primary"
                                size="md"
                                onClick={handleNestedOpenTransactions}
                                leftIcon={<ArrowCornerIcon />}
                            >
                                {nestedSelectedTransactionIds.length > 0
                                    ? `View ${nestedSelectedTransactionIds.length} selected`
                                    : "View all transactions"}
                            </Button>
                        )}
                    </div>
                </CommandIsland>
            ) : null;

        return historyRow ? (
            <HistoryCreatedTransactionDetails
                data={transactionSummaryData}
                footer={nestedTableFooter}
                historyRow={historyRow}
                rowSelection={nestedRowSelection}
                onRowSelectionChange={setNestedRowSelection}
            />
        ) : null;
    };

    return (
        <>
            <Drawer
                open={open}
                onClose={handleClose}
                direction="right"
                onAnimationEnd={setIsOpen}
                headerAddonLeft={headerAddonLeft}
                size="half"
                noPadding
            >
                {shouldShowContent && historyRow && (
                    <div className={styles.drawerContent}>
                        <div className={styles.content}>{renderContent()}</div>

                        <CommandIsland
                            sticky
                            className={
                                commandIslandStyles.bulkActionsStickyFooter
                            }
                        >
                            <div className={historyDetailStyles.footerLeft}>
                                {totalPages > 1 && (
                                    <Pagination
                                        size="md"
                                        currentPage={currentPage}
                                        pageCount={totalPages}
                                        onPageChange={setCurrentPage}
                                    />
                                )}
                                <CommandIslandTotal>
                                    {pluralizeWithCount(
                                        "activity",
                                        filteredLogs.length,
                                    )}
                                </CommandIslandTotal>
                            </div>
                        </CommandIsland>
                    </div>
                )}
            </Drawer>

            <Drawer
                open={!!expandedGroup}
                onClose={handleCloseExpandedGroup}
                direction="right"
                size="fullScreenWithNav"
                noPadding
                headerAddonLeft={
                    expandedGroup ? (
                        <StepTrailBreadcrumbs
                            steps={breadcrumbSteps}
                            currentStep="details"
                            onStepChange={handleCloseExpandedGroup}
                        />
                    ) : null
                }
            >
                {expandedGroup && (
                    <div className={styles.drawerContent}>
                        <div className={styles.content}>
                            {renderTransactionDetails()}
                        </div>
                    </div>
                )}
            </Drawer>
        </>
    );
};
